/**
 * Localized texts for Norwegian Nynorsk.
 *
 * Please maintain alphabetical order on variable name for ease of maintenance!
 */

import type { Translation } from './nb';

export const nn = {
  apiDocumentation: 'API-dokumentasjon',
  apiDocVardef: 'Variabeldefinisjonar',
  apiDocKlass: 'Klassifikasjonar',
  appSubTitle: ['Her samlar vi informasjon om våre datasett, variablar, klassifikasjonar og API-ar på ein stad.'],
  appTitle: 'SSB Dataportal',

  authentication: {
    logIn: 'Logg inn',
    logInSsbEmployee: 'Logg inn som SSB-tilsett',
    logOut: 'Logg ut',
    loginHeading: 'Innlogging',
    loginInfo:
      'Innlogging er kun tilgjengeleg for tilsette i Statistisk sentralbyrå. Du kan framleis bruke tenesta utan å logge inn.',
  },

  breadcrumbsLabel: 'Brødsmulesti',

  button: {
    removeFilter: 'Fjern alle filter',
  },

  by: 'av',

  classification: {
    about: {
      custodian: 'Ansvarleg',
      mail: 'E-post',
      publishedLanguages: 'Publiserte språk',
      basedOn: 'Basert på',
      legalBasis: 'Lovgrunnlag',
      publications: 'Publikasjonar',
      unitTypes: 'Einheitstypar',
      langEN: 'Engelsk',
      langNB: 'Bokmål',
      langNN: 'Nynorsk',
      langNO: 'Norsk',
      noChanges: 'Inga skildring av endringar innanfor denne versjonen.',
      changelog: 'Endringslogg',
      date: 'Dato',
      comment: 'Kommentar',
    },
    filterLevels: 'Filtrer på nivå',
    labelPlural: 'Klassifikasjonar',
    labelSingular: 'Klassifikasjon',
    label: 'Klassifikasjon',
    language: {
      notSelectedLanguage: 'Denne klassifikasjonen er ikkje tilgjengeleg på valt språk',
      missingInSelectedLanguage: 'Denne klassifikasjonen manglar innhald på valt språk, vel eit anna språk.',
      contentChangelog: 'Endringsloggen er kun tilgjengeleg på norsk',
    },
    type: 'Type',
    codelist: 'Kodeliste',
    standard: 'Standard',
    emailPlaceholder: 'Di e-postadresse',
    subscribe: 'Abonner',
    subscribeMessageError: 'Det oppstod ein feil under registrering',
    subscribeMessageAlready: 'Du er allereie abonnent',
    subscribeSubmit: 'Send inn',
    subscribeInfo: 'Abonner på oppdateringar for denne klassifikasjonen',
    subscribeMessageSuccess: 'Du vil motta ein e-post. Følg instruksjonane i e-posten for å starte abonnementet ditt.',
    subscribeMessageInvalidEmail: 'Skriv inn ei gyldig e-postadresse',
    download: {
      button: 'Last ned',
      formatLabel: 'Format',
      languageLabel: 'Språk',
      levelLabel: 'Nivå',
      allLevels: 'Alle nivåer',
      headingCodes: 'Last ned kodar til fil',
      headingChanges: 'Last ned endringar til fil',
      headingCorrespondence: 'Last ned korrespondansen til fil',
      copyLink: 'Kopier lenkje',
      linkCopied: 'Lenkje kopiert.',
      confirm: 'Last ned fil',
      inProgress: 'Lastar ned ...',
      error: 'Klarte ikkje laste ned filen. Prøv igjen.',
    },
    correspondence: {
      heading: 'Korrespondansar',
      info: 'Korrespondansetabellar viser samanhengen mellom to ulike kodeverk.',
      none: 'Denne versjonen har ingen korrespondansar',
      fromLevel: 'Nivå',
      toLevel: 'Nivå',
      owner: 'Eigar',
      id: 'ID',
      ownerSection: 'Eigarseksjon',
      responsible: 'Ansvarleg',
      codeSummary:
        'Korrespondansen koplar {sourceCount} kodar frå «{sourceName}» til {targetCount} kodar frå «{targetName}».',
      tableLabel: 'Korrespondansetabell',
      noTarget: 'Ingen tilsvarande kode',
    },
    variant: {
      id: 'ID',
      description: 'Skildring',
      responsible: 'Ansvarleg',
      ownerSection: 'Eigarseksjon',
      noVariants: 'Denne versjonen har ingen variantar',
      numberOfCodesAndLevels: 'Varianten inneheld {numberOfCodes} kodar fordelt over {numberOfLevels} {level}.',
      variantHeading: 'Variantar',
      variantInfo:
        'Ein variant baserer seg på ein klassifikasjonsversjon, og inneber ei omgruppering, utviding eller reduksjon av elementa i denne.',
    },
  },
  classificationDetails: {
    codes: 'Kodar',
    details: 'Detaljar',
    changes: 'Endringar',
    versions: 'Vel versjon',
    correspondences: 'Korrespondansar',
    variants: 'Variantar',
  },
  close: 'Lukk',
  codeTree: {
    label: 'Kodeliste',
    expand: 'Vis underkodar for',
    collapse: 'Skjul underkodar for',
    selectCode: 'Vel kode',
    filterLabel: 'Filtrer på kode eller namn',
    filterPlaceholder: 'Filtrer på kode eller namn',
    expandAll: 'Opne alle',
    collapseAll: 'Lukk alle',
    codeColumn: 'Kode',
    nameColumn: 'Namn',
    back: 'Tilbake',
    notesButtonLabel: 'Vis tilleggsinformasjon for',
    showShortNames: 'Vis kortnamn',
    shortNameColumn: 'Kortnamn',
    notesColumn: 'Tilleggsinformasjon',
  },
  codeSnippet: {
    codeExample: 'Kodeeksempel',
    daplaManual: 'Dapla-manualen',
    getVariableDefinition: 'Hent variabeldefinisjon med',
    linkToPyPiPackage: 'dapla-toolbelt-metadata (pypi.org)',
    daplaLab: 'Dapla Lab',
  },

  cookieBanner: {
    closeButtonLabel: 'Lukk knapp',
    label: 'Informasjonskapsel-kunngjering',
    message: 'Vi lagrar nødvendige informasjonskapslar som gjer at nettsida fungerer og er trygg.',
  },
  contact: {
    label: 'Kontakt',
  },
  copy: {
    code: 'Kopier kode',
    copied: 'Kopiert',
    id: 'Kopier ID',
    shortName: 'Kopier kortnamn',
    filePath: 'Kopier gs:// sti',
  },

  dataCoverageTimeline: {
    availableDataFileLabel: 'tilhøyrande datafil',
    availableDataFileLabelPlural: 'tilhøyrande datafiler',
    filePathLabel: 'Filsti',
    labelBimesterPrefix: 'B',
    labelFullYear: 'Heile',
    labelHalfYearPrefix: 'H',
    labelQuarterPrefix: 'K',
    labelTriannualPrefix: 'T',
    monthsShort: ['jan.', 'feb.', 'mars', 'apr.', 'mai', 'juni', 'juli', 'aug.', 'sep.', 'okt.', 'nov.', 'des.'],
    statusDataPresent: 'Data finst',
    statusMissingTargetSegment: 'Ingen fil med data for perioden',
    tooltipStatusDataPresent: 'Fil med data finst',
    tooltipStatusMissingTargetSegment: 'Ingen fil med data for perioden',
  },
  dataProduct: {
    labelPlural: 'Dataprodukt',
  },
  dataProductDetail: {
    dataProductFilters: 'Dataproduktfilter',
    dataset: 'Datasett',
    sortByMostNamingStandardViolations: 'Flest avvik frå namnestandard',
    noDatasetAvailable: 'Ingen datasett tilgjengeleg',
  },
  datasetDetail: {
    aboutDataset: 'Om datasettet',
    dataProduct: 'Dataprodukt',
    bucket: 'Bøtte',
    datasetState: 'Datatilstand',
    assessment: 'Vurdering',
    responsible: 'Ansvarleg team',
    id: 'ID',
    namingStandardViolations: 'Tal på avvik frå namnestandard',
    dataFiles: 'Datafiler',
    dataCoverageTimeline: 'Tilgjengelege periodar',
  },
  editing: {
    created: 'Oppretta',
    updated: 'Sist oppdatert',
  },

  error: {
    classificationDetailsTabs: {
      notFoundCodes: 'Kodar ikkje funne',
      notFoundAboutClassification: 'Om klassifikasjonen ikkje funnen',
      notFoundChanges: 'Kodeendringar ikkje funne',
      notFoundVersions: 'Versjonar ikkje funne',
      notFoundCorrespondences: 'Korrespondansar ikkje funne',
      notFoundVariants: 'Variantar ikkje funne',
    },
    somethingWentWrong: 'Beklagar, noko gjekk gale.',
    unauthorized: 'Beklagar, du har ikkje tilgang.',
    technicalProblemsTitle: 'Vi har tekniske problem',
    technicalProblemsMessage: 'Dette kjem ikkje av noko du gjorde. Vent litt og prøv igjen.',
    reloadPage: 'Last sida på nytt',
    goBack: 'Gå tilbake',
    goHome: 'Gå til framsida',
    helpTitle: 'Du kan prøve å:',
    helpReload: 'vente litt og laste sida på nytt',
    helpBack: 'gå tilbake til førre side',
    helpHome: 'gå til framsida',
    helpChangeFilters: 'endre søk eller filter',
    helpRegisterChangeTable: 'registrere ei endringstabell',
    missingChangeTable: '{sourceName} har inga endringstabell mot: {targetName}',
    statusCode: 'Feilkode',
    referenceCode: 'Referanse',
    supportPrefix: 'Har problemet vart ei stund, kan du',
    supportLinkText: 'kontakte oss',
    brokenLinkMailSubject: 'Øydelagd lenkje i SSB Dataportal',
    brokenLinkMailBody: (path: string) =>
      [
        'Hei, eg vil melde frå om ei mogleg øydelagd lenkje i SSB Dataportal.',
        '',
        `Side: ${path}`,
        '',
        'Skildring av kva som ikkje fungerte:',
      ].join('\n'),
    notFoundDataProductDetails: 'Dataprodukt ikkje funnen',
    notFoundTitleDataProductDetails: 'Dataprodukt ikkje funnen',
    notFoundMessageDataProductDetails: 'Er det skrivefeil i lenkja? Eller har dataproduktet blitt sletta eller flytta?',
    notFoundHelpListDataProductDetails: [
      'sjekke at du har riktig dataprodukt-id i lenkja',
      'gå til oversikta over dataprodukt',
      'gå til framsida',
    ],
    notFoundTitleDataset: 'Datasett ikkje funnen',
    notFoundMessageDataset: 'Er det skrivefeil i lenkja? Eller har datasettet blitt sletta eller flytta?',
    notFoundHelpListDataset: [
      'sjekke at du har riktig datasett-id i lenkja',
      'gå til oversikta over datasett',
      'gå til oversikta over dataprodukt',
    ],
    notFoundHelpListClassificationDetails: [
      'sjekke at du har riktig klassifikasjon-id i lenkja',
      'gå til oversikta over klassifikasjonar',
      'gå til framsida',
    ],
    notFoundHelpListClassificationId: [
      'sjekke at du har riktig klassifikasjon-id i lenkja',
      'gå til oversikta over klassifikasjonar',
    ],
    notFoundTitleClassificationDetails: 'Klassifikasjon ikkje funnen',
    notFoundMessageClassificationDetails:
      'Er det skrivefeil i lenkja? Eller har klassifikasjonen blitt sletta eller flytta?',
    notFoundTitleVersionDetails: 'Versjon ikkje funnen',
    notFoundMessageVersionDetails: 'Er det skrivefeil i lenkja? Eller har versjonen blitt sletta eller flytta?',
    notFoundHelpListVersionDetails: [
      'sjekke at du har riktig versjons-id i lenkja',
      'gå tilbake til klassifikasjonen',
      'gå til oversikta over klassifikasjonar',
    ],
    notFoundTitleVariantDetails: 'Variant ikkje funnen',
    notFoundMessageVariantDetails: 'Er det skrivefeil i lenkja? Eller har varianten blitt sletta eller flytta?',
    notFoundHelpListVariantDetails: [
      'sjekke at du har riktig variant-id i lenkja',
      'gå til oversikta over variantar',
      'gå til oversikta over klassifikasjonar',
    ],
    notFoundTitleCorrespondenceDetails: 'Korrespondansetabell ikkje funnen',
    notFoundMessageCorrespondenceDetails:
      'Er det skrivefeil i lenkja? Eller har korrespondansetabellen blitt sletta eller flytta?',
    notFoundHelpListCorrespondenceDetails: [
      'sjekke at du har riktig korrespondansetabell-id i lenkja',
      'gå til oversikta over korrespondansar',
      'gå til oversikta over klassifikasjonar',
    ],
    notFoundTitle: 'Sida finst ikkje',
    notFoundMessage: 'Sida kan vere flytta, sletta eller lenkja kan vere feil.',
    notFoundTitleVariableDetails: 'Variabeldefinisjon ikkje funnen',
    notFoundMessageVariableDetails:
      'Er det skrivefeil i lenkja? Eller har variabeldefinisjonen blitt sletta eller flytta?',
    notFoundHelpListVariableDetails: [
      'sjekke at du har riktig variabeldefinisjon-kortnamn i lenkja',
      'gå til oversikta over variabeldefinisjonar',
      'gå til framsida',
    ],
    reportBrokenLink: 'Meld frå om øydelagd lenkje',
  },
  from: 'Frå',
  info: {
    aboutDataportal: {
      body: [
        'Målet er å gjere det enklare å finne, forstå og bruke SSBs data på ein korrekt og effektiv måte.',
        'I denne fyrste versjonen kan du utforske SSBs variabeldefinisjonar. Portalen blir gradvis utvida med oversikt over datasett, klassifikasjonar og API-ar, slik at du kan sjå heile samanhengen frå konsept til ferdig datafil. Ved å koble definisjonar frå Vardef med kodeverk frå Klass og dokumentasjon om datasett, etablerer vi ei "felles sannheit" som sikrar at vi alltid tolkar og bruker dataa våre på same måte.',
      ],
      title: 'Om SSB Dataportal',
      toggle: 'Kvifor SSB Dataportal',
    },
    datasetPrototypeIntro: 'Under utvikling',
    datasetPrototypeInfo:
      'Denne sida skildrar eit utval av data i SSB. Utviklinga held fram med fleire datatypar og meir detaljerte skildringar.',
    feedbackTitle: 'Del erfaringane dine!',
    feedbackBody: 'Vi jobbar kontinuerleg med å forbetre dataportalen, og du kan hjelpe oss ved å fylle ut',
    feedbackForm: 'tilbakemeldingsskjema for SSB Dataportal',
    footerAboutPage: 'Om nettstaden',
    footerContact: 'Kom i kontakt',
    footerAccessibilityStatement: 'Tilgjengelegheitserklæring',
    footerPrivacyStatement: 'Personvernerklæring',
    landingPageSubTitle: 'Di inngang til SSBs felles kunnskap om data',
  },
  loading: {
    filters: 'Lastar filter ...',
    results: 'Lastar resultat ...',
  },
  migrationClassifications: {
    header: 'Klass er flytta frå ssb.no/klass',
    info: 'Du er no på den nye tenesta Klass i SSB Dataportal. Oppdater gjerne bokmerke og lagra lenkjer.',
  },
  migrationVariableDefinitions: {
    header: 'Migrering av variabeldefinisjonar pågår',
    info: 'Vi er i gang med å flytte variabeldefinisjonar til SSB Dataportal. Inntil arbeidet er fullført vil noko innhald framleis ligge på den gamle sida.',
    linkText: 'Finn fleire variabeldefinisjonar på ssb.no',
  },
  navigateHome: 'Naviger til framsida',
  next: 'Neste',
  no: 'Nei',
  noDataPlaceholder: '—',
  on: 'på',
  opensInNewTab: 'opnast i ny fane',
  owner: {
    daplaTeam: 'Dapla Team',
    groups: 'Grupper',
  },
  pageTitle: {
    classifications: 'Klassifikasjoner',
    dataProducts: 'Dataprodukter',
    variableDefinitions: 'Variabeldefinisjonar',
  },
  previous: 'Førre',
  products: {
    assessment: {
      filterLabel: 'Verdivurdering',
      open: 'Open',
      protected: 'Skjerma',
      sensitive: 'Sensitiv',
    },
    datasetState: {
      processedData: 'Klargjorde data',
      outputData: 'Utdata',
      inputData: 'Inndata',
      statistics: 'Statistikk',
      sourceData: 'Kjeldedata',
    },
    other: 'Anna dataprodukt',
    statistic: 'Statistikkprodukt',
    storageCategory: {
      filterLabel: 'Lagringskategori',
      shared: 'Delt',
      product: 'Produkt',
      source: 'Kjelde',
    },
    typeFilterLabel: 'Dataprodukttype',
    unknown: 'Ukjend produkttype',
  },
  search: {
    hits: 'treff',
    label: 'Søk',
    noHits: 'Søket ditt ga ingen treff',
    variableDefinitions: 'Søketreff variabeldefinisjonar',
    classifications: 'Søketreff klassifikasjonar',
    textFilter: {
      label: 'Filtrer på namn',
      tagLabel: 'Namn:',
      inputId: 'søk-input',
    },

    filter: {
      close: 'Lukk filter',
      label: 'Filter',
      filterAndSearch: 'Filter og søk',
      open: 'Opne filter',
    },

    sort: {
      label: 'Sortering',
      lastUpdatedFirst: 'Sist endra først',
      titleAlphabeticalAsc: 'Tittel A-Å',
      titleAlphabeticalDesc: 'Tittel Å-A',
    },
  },

  filterTag: {
    remove: 'Fjern',
    listLabel: 'Liste over valde filter',
    sectionLabel: 'Aktive søkjefilter',
  },

  ssbDataportal: 'SSB Dataportal',
  statisticsNorway: 'Statistisk sentralbyrå',

  status: {
    draft: 'Utkast',
    label: 'Status',
    publishedExternal: 'Publisert eksternt',
    publishedInternal: 'Publisert internt',
  },

  subjectArea: 'Statistikkområde',
  subjectFields: 'Statistikkområde',

  tabs: {
    ariaLabel: 'Navigasjon katalogar',
    classifications: 'Klassifikasjonar',
    dataProducts: 'Dataprodukter',
    variableDefinitions: 'Variabeldefinisjonar',
  },

  to: 'Til',
  unitTypes: 'Einingstypar',
  yes: 'Ja',

  validity: {
    validityPeriods: 'Gyldigheitsperioder',
    chooseValidityPeriod: 'Vel gyldigheitsperiode',
    validityPeriodsInfo:
      'Dersom ein variabeldefinisjon endrar tyding, kan ein avgrense dei ulike tydingane til tidsperiodar. Her kan ein byte mellom gyldigheitsperiodar ved å velje datoen perioden gjeld frå.',
    validFrom: 'Gyldig frå',
    validTo: 'Gyldig til',
  },

  variableDefinition: {
    aboutVariable: 'Om variabelen',
    comment: 'Kommentar',
    contact: 'Kontakt',
    documentation: 'Dokumentasjon',
    externalPersonalData: 'Sensitive personopplysningar',
    id: 'ID',
    internalPersonalData: 'Inneheld særlege kategoriar av personopplysningar',
    labelPlural: 'Variabeldefinisjonar',
    mail: 'E-post',
    owner: 'Eigar',
    unitTypeInfo:
      'Ein einingstype er typen objekt (einingar) det blir laga statistikk om, til dømes person, føretak og val',
    shortName: 'Kortnamn',
  },
  versions: {
    name: 'Namn',
    invert: 'Inverter tabell',
    isNotValid: 'Versjonen er ikkje gyldig på dagens dato.',
    noChanges: 'Ingen kodeendringar finst for den aktuelle versjonen.',
    numberOfCodesAndLevels: 'Versjonen inneheld {numberOfCodes} kodar fordelt over {numberOfLevels} {level}.',
    level: 'nivå',
    levelPlural: 'nivåer',
    codeChangesForVersion: '{numberOfChanges} kodeendringar frå forrige versjon.',
    codeChangesTableLabel: 'Tabell over kodeendringar',
  },
} satisfies Translation;
